import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import TermsModal from './termModal';
import TermInstructionsDialog from './termInstructionsDialog';
import TermDeleteDialog from './termDeleteDialog';
import Loading from '../../components/loading';
import SuccessMessage from '../../components/successMessage';
import ErrorMessage from '../../components/errorMessage';
import { useTermsScreen } from './hooks/useTermsScreen';

export default function TermsMobile() {
  const {
    terms,
    isLoading,
    deleteTerm,
    downloadTerm,
    onSavedTerm,
    showSuccessMessage,
    setShowSuccessMessage,
    successMessage,
    showErrorMessage,
    setShowErrorMessage,
    errorMessage,
  } = useTermsScreen();

  const [openInstructions, setOpenInstructions] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [termToDelete, setTermToDelete] = useState(null);

  const menuOpen = (event, term) => {
    setAnchorEl(event.currentTarget);
    setSelectedTerm(term);
  };

  const menuClose = () => {
    setAnchorEl(null);
    setSelectedTerm(null);
  };

  const closingInstructions = () => {
    setOpenInstructions(false);
    setOpenModal(true);
  };

  const download = () => {
    if (selectedTerm) {
      downloadTerm(selectedTerm.id);
    }
    menuClose();
  };

  const handleDeleteClick = () => {
    setTermToDelete(selectedTerm);
    setAnchorEl(null);
  };

  const handleConfirmDelete = () => {
    deleteTerm(termToDelete.id).then(() => {
      setTermToDelete(null);
      setSelectedTerm(null);
    });
  };

  const savedTerm = (returnFromApi) => {
    onSavedTerm(returnFromApi);
    setOpenModal(false);
  };

  return (
    <>
      <Loading isToHidden={isLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Configuração de Termos
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 2 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenInstructions(true)}
          >
            Adicionar Termo
          </Button>
        </Box>

        <Divider />

        {terms.length === 0 && !isLoading ? (
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              Nenhum termo cadastrado ainda.
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {terms.map((term, index) => (
              <React.Fragment key={term.id}>
                <ListItem
                  sx={{ py: 1.5, px: 2 }}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label="ações do termo"
                      onClick={(event) => menuOpen(event, term)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={{ overflowWrap: 'anywhere' }}
                      >
                        {term.description}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        Cadastrado em {term.date}
                      </Typography>
                    }
                    slotProps={{ secondary: { component: 'div' } }}
                  />
                </ListItem>
                {index < terms.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Card>

      <Menu id="actions-menu" anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={menuClose}>
        <MenuItem onClick={download}>Baixar .docx</MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          Excluir
        </MenuItem>
      </Menu>

      <TermDeleteDialog
        open={Boolean(termToDelete)}
        termDescription={termToDelete?.description}
        onCancel={() => {
          setTermToDelete(null);
          setSelectedTerm(null);
        }}
        onConfirm={handleConfirmDelete}
      />

      <TermInstructionsDialog open={openInstructions} onClose={closingInstructions} fullScreen />

      <TermsModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSave={savedTerm}
        currentTerm={null}
      />
    </>
  );
}
